'use client';

import styled from 'styled-components';
import { ReactNode } from 'react';
import { JSX } from 'react';
import { theme } from '@/config/theme';

type TypographyVariant = 
  | 'heading-large'
  | 'body-regular'
  | 'label-uppercase'
  | 'metric-value'
  | 'body-semibold'
  | 'body-bold';

interface TypographyProps {
  variant: TypographyVariant;
  children: ReactNode;
  className?: string;
  as?: keyof JSX.IntrinsicElements;
  align?: 'left' | 'center' | 'right';
}

const getTypographyStyles = (variant: TypographyVariant) => {
  const styles = {
    'heading-large': `
      font-family: 'Open Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
      font-weight: 700;
      font-size: 20px;
      line-height: 100%;
      letter-spacing: 0px;
      color: ${theme.colors.black};
    `,
    'body-regular': `
      font-family: 'Open Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
      font-weight: 400;
      font-size: 13px;
      line-height: 100%;
      letter-spacing: 0px;
      color: ${theme.colors.gray[550]};
    `,
    'label-uppercase': `
      font-family: 'Plus Jakarta Sans', 'Open Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
      font-weight: 500;
      font-size: 11px;
      line-height: 18px;
      letter-spacing: 1px;
      text-transform: uppercase;
      color: ${theme.colors.gray[500]};
    `,
    'metric-value': `
      font-family: 'Plus Jakarta Sans', 'Open Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
      font-weight: 700;
      font-size: 24px;
      line-height: 32px;
      letter-spacing: 0px;
      vertical-align: bottom;
      color: ${theme.colors.gray[900]};
    `,
    'body-semibold': `
      font-family: 'Open Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
      font-weight: 600;
      font-size: 12px;
      line-height: 20px;
      letter-spacing: 0px;
      color: ${theme.colors.gray[800]};
    `,
    'body-bold': `
      font-family: 'Open Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
      font-weight: 700;
      font-size: 14px;
      line-height: 16px;
      letter-spacing: 0px;
      color: ${theme.colors.gray[900]};
    `,
  };

  return styles[variant];
};

const StyledTypography = styled.span<{ 
  $variant: TypographyVariant; 
  $align?: 'left' | 'center' | 'right';
}>`
  ${({ $variant }) => getTypographyStyles($variant)}
  ${({ $align }) => $align && `text-align: ${$align};`}
  margin: 0;
  padding: 0;
`;

export const Typography: React.FC<TypographyProps> = ({
  variant,
  children,
  className,
  as = 'span',
  align,
}) => {
  return (
    <StyledTypography
      as={as}
      $variant={variant}
      $align={align}
      className={className}
    >
      {children}
    </StyledTypography>
  );
};

// Convenience components for common use cases
export const Heading = ({ children, align, className }: { 
  children: ReactNode; 
  align?: 'left' | 'center' | 'right';
  className?: string;
}) => (
  <Typography variant="heading-large" as="h1" align={align} className={className}>
    {children}
  </Typography>
);

export const BodyText = ({ children, className }: { 
  children: ReactNode; 
  className?: string;
}) => (
  <Typography variant="body-regular" as="p" className={className}>
    {children}
  </Typography>
);

export const Label = ({ children, className }: { 
  children: ReactNode; 
  className?: string;
}) => (
  <Typography variant="label-uppercase" as="span" className={className}>
    {children}
  </Typography>
);

export const MetricValue = ({ children, className }: { 
  children: ReactNode; 
  className?: string;
}) => (
  <Typography variant="metric-value" as="span" className={className}>
    {children}
  </Typography>
);

export const BoldText = ({ children, className }: { 
  children: ReactNode; 
  className?: string;
}) => (
  <Typography variant="body-bold" as="span" className={className}>
    {children}
  </Typography>
);

export const SemiBoldText = ({ children, className }: { 
  children: ReactNode; 
  className?: string;
}) => (
  <Typography variant="body-semibold" as="span" className={className}>
    {children}
  </Typography>
);
